import { Box, Menu, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { FamilyMember } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { NameFormat, formatName } from 'src/utils/nameFormatting';
import classes from './FamilyIndividualToggle.module.css';

type FamilyIndividualView = 'family' | 'individual';

type SegmentPaddingX = { left: number; right: number };

const DEFAULT_CHOSEN_SEGMENT_PADDING_X: SegmentPaddingX = { left: 55, right: 55 };

type Props = {
  value: FamilyIndividualView;
  onChange: (value: FamilyIndividualView) => void;
  familyLabel?: string;
  individualLabel?: string;
  familyTooltip?: string;
  members?: FamilyMember[];
  onMemberSelect?: (memberId: string) => void;
  chosenSegmentPaddingX?: SegmentPaddingX;
  showGenericInactiveLabel?: boolean;
};

export function FamilyIndividualToggle({
  value,
  onChange,
  familyLabel = 'Family',
  individualLabel = 'Individual',
  familyTooltip,
  members,
  onMemberSelect,
  chosenSegmentPaddingX = DEFAULT_CHOSEN_SEGMENT_PADDING_X,
  showGenericInactiveLabel = false,
}: Props) {
  return (
    <ProfileToggle
      value={value}
      onChange={onChange}
      familyLabel={familyLabel}
      individualLabel={individualLabel}
      familyTooltip={familyTooltip}
      members={members}
      onMemberSelect={onMemberSelect}
      chosenSegmentPaddingX={chosenSegmentPaddingX}
      showGenericInactiveLabel={showGenericInactiveLabel}
    />
  );
}

const profileSegmentBoxStyle = {
  display: 'flex',
  borderRadius: 20,
  padding: 3,
  gap: 0,
} as const;

// The pill background is a brand.0 → brand.6 gradient with the brand.6 end always
// sitting behind the currently chosen segment, so the stronger colour anchors the
// selection regardless of which side is active. brand.0 holds across most of the
// pill so the whitish end dominates and the orange stays concentrated behind the
// chosen option.
function profileGradient(value: FamilyIndividualView): string {
  const direction = value === 'family' ? 'to left' : 'to right';
  return `linear-gradient(${direction}, var(--mantine-color-brand-0) 45%, var(--mantine-color-brand-6) 100%)`;
}

// The chosen Individual pill is a fixed width so its name hugs the left and the dropdown
// arrow hugs the right with a gap between; the Family pill and unchosen segments shrink-wrap.
const CHOSEN_INDIVIDUAL_PILL_WIDTH = 306;

function profileSegmentStyle(active: boolean, chosenPaddingX: SegmentPaddingX) {
  return {
    paddingLeft: active ? chosenPaddingX.left : 20,
    paddingRight: active ? chosenPaddingX.right : 16,
    paddingTop: 10,
    paddingBottom: 10,
    borderRadius: 16,
    whiteSpace: 'nowrap',
  } as const;
}

function profileDropdownSegmentStyle(active: boolean) {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: active ? CHOSEN_INDIVIDUAL_PILL_WIDTH : undefined,
    paddingLeft: 20,
    paddingRight: 16,
    paddingTop: 10,
    paddingBottom: 10,
    borderRadius: 16,
    whiteSpace: 'nowrap',
  } as const;
}

type ProfileToggleProps = {
  value: FamilyIndividualView;
  onChange: (value: FamilyIndividualView) => void;
  familyLabel: string;
  individualLabel: string;
  familyTooltip?: string;
  members?: FamilyMember[];
  onMemberSelect?: (memberId: string) => void;
  chosenSegmentPaddingX: SegmentPaddingX;
  showGenericInactiveLabel: boolean;
};

// A single stable structure for the profile toggle: the family/individual
// buttons stay mounted regardless of whether the member dropdown is available
// yet. Swapping between separate components (e.g. a SegmentedControl and a
// custom menu) while family data loads would replace the DOM node mid-click and
// drop the first press.
function ProfileToggle({
  value,
  onChange,
  familyLabel,
  familyTooltip,
  individualLabel,
  members,
  onMemberSelect,
  chosenSegmentPaddingX,
  showGenericInactiveLabel,
}: ProfileToggleProps) {
  const showMemberDropdown = !!(members && members.length > 0 && onMemberSelect);

  // When enabled, only the selected side shows its specific name and the unselected
  // side shows a generic label, so choosing John Henderson reads "Family / John
  // Henderson" and choosing his family reads "Henderson Family / Individual". When
  // disabled (e.g. the clients list) both sides keep their labels regardless of
  // selection, so it stays "Families / Individuals".
  const displayFamilyLabel =
    showGenericInactiveLabel && value !== 'family' ? 'Family' : familyLabel;
  const displayIndividualLabel =
    showGenericInactiveLabel && value !== 'individual' ? 'Individual' : individualLabel;

  return (
    <Box style={{ ...profileSegmentBoxStyle, background: profileGradient(value) }}>
      <UnstyledButton
        onClick={() => onChange('family')}
        className={value === 'family' ? classes.profileSegmentActive : classes.profileSegment}
        style={profileSegmentStyle(value === 'family', chosenSegmentPaddingX)}
      >
        {familyTooltip ? (
          <Tooltip label={familyTooltip} withArrow>
            <span>{displayFamilyLabel}</span>
          </Tooltip>
        ) : (
          displayFamilyLabel
        )}
      </UnstyledButton>

      {showMemberDropdown ? (
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <UnstyledButton
              className={
                value === 'individual' ? classes.profileSegmentActive : classes.profileSegment
              }
              style={profileDropdownSegmentStyle(value === 'individual')}
            >
              {displayIndividualLabel}
              <IconChevronDown size={20} />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {members.map(member => (
              <Menu.Item key={member.clientId} onClick={() => onMemberSelect(member.clientId)}>
                {formatName(member.client.firstName, member.client.lastName, NameFormat.NATURAL)}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      ) : (
        <UnstyledButton
          onClick={() => onChange('individual')}
          className={value === 'individual' ? classes.profileSegmentActive : classes.profileSegment}
          style={profileSegmentStyle(value === 'individual', chosenSegmentPaddingX)}
        >
          {displayIndividualLabel}
        </UnstyledButton>
      )}
    </Box>
  );
}
